import {useCallback, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, Stack} from "@mui/material";
import {Paper, useTheme} from "@mui/material/index";
import {FormProvider, useForm} from "react-hook-form";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Attachment, IExpiredActionExecuteParams, IExpiredRecoveryEntrustmentForm} from "../models/models";
import ContentBody from "../common/content-body/ContentBody";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {EXPIRED_PRACTICES_QUERY_KEY, EXPIRED_RECOVERY_ENTRUSTMENT_FORM_VALIDATION_SCHEMA, EXPIRED_RECOVERY_ENTRUSTMENT_INITIAL_VALUE, ExpiredInsertionAction, MAIL_MAX_CHAR} from "../config";
import RecoveryEntrustmentDetail from "./recovery-entrustment-detail/RecoveryEntrustmentDetail";
import EntrustmentAttachments from "./entrustment-attachments/EntrustmentAttachments";
import UploadAttachment from "../common/upload-attachment/UploadAttachment";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {setSnackbar} from "../../../util/state/globalAction";
import {executeExpiredAction, findRecoveryCompany, getExpiredActionsMessageTemplate} from "../services/http.services";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import {excludedExpiredRecoveratorAttachmentState, expiredActionPropsState, practiceDetail, recoveryCompaniesState, recoveryEntrustmentDetailState} from "../state";
import CustomLoader from "../../../util/component/CustomLoader";
import { yupResolver } from "@hookform/resolvers/yup";

interface RecoveryEntrustmentProps {
    onConfirm: () => void,
    onClose: () => void
}

const RecoveryEntrustment = ({...props}: RecoveryEntrustmentProps) => {
    const {t} = useTranslation(['expiredManagement', 'translation']);
    const theme = useTheme();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    
    const [recoveryEntrustmentDetail, setRecoveryEntrustmentDetail] = useRecoilState(recoveryEntrustmentDetailState);
    const [recoveryCompanies, setRecoveryCompanies] = useRecoilState(recoveryCompaniesState);
    const [isMultipleCodes, setIsMultipleCodes] = useState(false); 
    const excludedAttachments = useRecoilValue(excludedExpiredRecoveratorAttachmentState);

    const expiredActionProps = useRecoilValue(expiredActionPropsState);
    const setPracticeDetail = useSetRecoilState(practiceDetail);

    const recoveryEntrustmentForm = useForm<IExpiredRecoveryEntrustmentForm>({
        defaultValues: EXPIRED_RECOVERY_ENTRUSTMENT_INITIAL_VALUE,
        resolver: yupResolver(EXPIRED_RECOVERY_ENTRUSTMENT_FORM_VALIDATION_SCHEMA)
    });

    const viewSnackbarMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message, t('common.message.error.tryLater')),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const findRecoveryCompaniesQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.FIND_RECOVERY_COMPANIES],
        findRecoveryCompany,
        {
            enabled: false,
            onSuccess: (response) => {
                setRecoveryCompanies(response?.data);
            }
        }
    );

    const getActionMessageTemplateQuery = useQuery(
        [`getActionMessageTemplateQuery-${expiredActionProps?.expiredCodes}`],
        () => {
            if(!!expiredActionProps?.expiredCodes && expiredActionProps?.expiredCodes.length > 0)
                return getExpiredActionsMessageTemplate(expiredActionProps?.expiredCodes, ExpiredInsertionAction.RECOVERY);
        },
        {
            enabled: false,
            onSuccess: (response) => {
                setRecoveryEntrustmentDetail(response.data);

            },
            onError: (error: any) => {

                viewSnackbarMessage(`common.message.error.getActionTemplate.${error?.response?.status}`);
                props.onClose();
            }
        });

    const executeMutation = useMutation(
        {
            mutationKey: [EXPIRED_PRACTICES_QUERY_KEY.EXECUTE_ACTION],
            mutationFn: (params: IExpiredActionExecuteParams) => {
                return executeExpiredAction(params);
            },
            onSuccess: (response) => {
                viewSnackbarMessage('common.message.success', snackbarTypes.success);

                if (!!response?.data && !isMultipleCodes)
                    setPracticeDetail(response.data);

                props.onConfirm();
            },
            onError: (error: any) => {
                viewSnackbarMessage(`common.message.error.execute.${error?.response?.status}`, snackbarTypes.error);
            }
        }
    )

    const setFormFieldsValues = (data: any) => {

        recoveryEntrustmentForm.setValue('recoveryCompany', data.recoveryCompany || null);
        recoveryEntrustmentForm.setValue('to', data.recoveryCompany?.email || '');
        recoveryEntrustmentForm.setValue('object', data.subject);
        recoveryEntrustmentForm.setValue('message', data.body);
        recoveryEntrustmentForm.setValue('rentalType', expiredActionProps?.raDetails?.rentalType || '');
        recoveryEntrustmentForm.setValue('attachmentList', data?.attachment ? data?.attachment?.map((attach: any) => ({
            code: attach.id, //Mapping necessario in quanto il campo id viene sovrascritto con l'id autogenerato dello useFieldArray
            filename: attach.filename,
            url: attach.url
        })) : []);
    }

    const refreshData = (data: any[]) => {

        if(!!data && data.length>0) {

            const list: Attachment[] = data.filter((attach: any) => !!attach.id && !excludedAttachments.includes(attach.id)).map(attach => ({
                code: attach.id,
                filename: attach.filename,
                url: attach.url,
            }))

            recoveryEntrustmentForm.setValue('attachmentList',  list);
        }
    }

    const onConfirm = useCallback((formData: IExpiredRecoveryEntrustmentForm) => {

        const req: IExpiredActionExecuteParams = {
            permissionData: {
                mobilityStores: globalData?.user?.mobilityStores?.map(((elem:any) => elem.sapId)),
                authorities: globalData?.user?.scopes
            },
            action: expiredActionProps?.actionType || '',
            expiredCodes: expiredActionProps?.expiredCodes || [],
            subject: formData.object || undefined,
            body: formData.message || '',
            templateCode: recoveryEntrustmentDetail?.code || '',
            language: recoveryEntrustmentDetail?.language || '',
            attachment: formData.attachmentList.map(attach => ({
                id: attach.code,
                filename: attach.filename,
                url: attach.url
            })),
            recoveryCompany: {
                name: formData.recoveryCompany.name || '',
                tenant: formData.recoveryCompany.tenant || '',
                amount: formData.recoveryCompany.amount || '0',
                email: formData.recoveryCompany.email || '',
            }
        }

        executeMutation.mutate(req);

    }, [globalData, expiredActionProps, recoveryEntrustmentDetail]);

    useEffect(() => {

        if (!!recoveryEntrustmentDetail)
            setFormFieldsValues(recoveryEntrustmentDetail);

    }, [recoveryCompanies, recoveryEntrustmentDetail]);

    useEffect(()=> {
        if (!!expiredActionProps) {

            (expiredActionProps.expiredCodes.length > 1) && setIsMultipleCodes(true)
            getActionMessageTemplateQuery.refetch();

            if (recoveryCompanies.length === 0) {
                findRecoveryCompaniesQuery.refetch();
            }
        }
    },[]);

    return (
        <>
            <FormProvider {...recoveryEntrustmentForm}>
                <ContentBody>
                    <Paper elevation={1} sx={{p: theme.spacing(2)}}>
                        <RecoveryEntrustmentDetail forView={isMultipleCodes}/>

                        {!isMultipleCodes &&
                            !!recoveryEntrustmentDetail  && recoveryEntrustmentDetail.attachment.length > 0 &&
                            <Box pt={theme.spacing(2)}>
                                <hr/>

                                <EntrustmentAttachments />
                            </Box>
                        }

                        <Stack direction={{sm: 'row'}} gap={1}
                               sx={{display: "flex", justifyContent: "flex-end", mt: theme.spacing(2), mb: theme.spacing(1)}}>

                            {!isMultipleCodes &&
                                <UploadAttachment expiredCode={expiredActionProps?.expiredCodes?.at(0) || ''} postAction={refreshData}/>
                            }

                            <Button onClick={recoveryEntrustmentForm.handleSubmit(onConfirm)}>
                                {t('recoveryEntrustment.button.save')}
                            </Button>
                        </Stack>
                    </Paper>
                </ContentBody>
            </FormProvider>

            <CustomLoader loading={getActionMessageTemplateQuery.isFetching || findRecoveryCompaniesQuery.isFetching || executeMutation.isLoading} />
        </>
    )
}

export default RecoveryEntrustment;